import { useSignalRConnection } from './useSignalRConnection';

const CONNECT_RETRY_MS = 2_000;
const CONNECT_MAX_ATTEMPTS = 3;

export function useChatSignalRConnection() {
    const connection = useSignalRConnection('chat');
    let joinedChatId: string | undefined;

    let switchChatQueue: Promise<void> = Promise.resolve();

    // Queues job behind whatever's already running, so stop()/start()/invoke calls on the shared
    // connection never interleave (SignalR throws if start() runs while another stop() is still
    // pending). The queue survives a failed job so one failure can't jam every later one.
    const enqueue = <T>(job: () => T | Promise<T>): Promise<T> => {
        const run = switchChatQueue.then(job);
        switchChatQueue = run.then(
            () => undefined,
            () => undefined
        );
        return run;
    };

    // A failed JoinChat leaves the connection open but out of the group: retry rather than lose
    // the answer silently (the store's own fallback poll covers the rest).
    const rejoinChat = (chat_id: string, attempt: number) => {
        // The chat can change (a switch, or disconnect()) before this retry fires.
        if (joinedChatId !== chat_id) {
            return;
        }

        // Re-checked inside the queued job, not just above: a switchChat queued ahead of this
        // can change joinedChatId before this callback actually runs.
        const run = enqueue(() => {
            if (joinedChatId !== chat_id) {
                return undefined;
            }
            return connection.invoke('JoinChat', chat_id);
        });

        run.catch((err) => {
            console.error('Failed to rejoin chat:', chat_id, err);
            if (attempt < CONNECT_MAX_ATTEMPTS) {
                setTimeout(() => rejoinChat(chat_id, attempt + 1), CONNECT_RETRY_MS);
            }
        });
    };

    connection.onreconnected(async () => {
        if (joinedChatId) {
            console.log('Reconnected, rejoining chat:', joinedChatId);
            rejoinChat(joinedChatId, 1);
        }
    });

    // Set around every deliberate stop() so onclose below can tell "we tore this down on purpose"
    // (a chat switch, disconnect()) apart from SignalR giving up on its own reconnect schedule.
    let isDisconnectingIntentionally = false;

    const stopConnection = async () => {
        isDisconnectingIntentionally = true;
        try {
            await connection.stop();
        } finally {
            isDisconnectingIntentionally = false;
        }
    };

    // withAutomaticReconnect() gives up permanently after its retry schedule is exhausted
    // (~42s by default) and calls onclose instead of retrying again: without this, the
    // connection stays dead for the rest of the page's life.
    connection.onclose(() => {
        if (isDisconnectingIntentionally) {
            return;
        }

        console.warn('Connection closed permanently, restarting');
        // Queued like every other stop()/start(): otherwise this can race a switchChat/disconnect
        // that's mid-restart on the same connection when automatic reconnect gives up.
        enqueue(() => startConnection())
            .then(() => {
                if (joinedChatId) {
                    rejoinChat(joinedChatId, 1);
                }
            })
            .catch((err) => {
                console.error('Failed to restart connection after it closed:', err);
            });
    });

    const startConnection = async (attempt = 1): Promise<void> => {
        try {
            await connection.start();
        } catch (err) {
            if (attempt >= CONNECT_MAX_ATTEMPTS) {
                throw err;
            }
            await new Promise((resolve) => setTimeout(resolve, CONNECT_RETRY_MS));
            return startConnection(attempt + 1);
        }
    };

    const runSwitchChat = async (
        newChatId?: string,
        oldChatId?: string,
        isStale?: () => boolean
    ) => {
        // A newer switchChat call already queued behind this one: skip straight to it.
        if (isStale?.()) {
            return;
        }

        if (connection.state !== 'Disconnected') {
            await stopConnection();
        }

        // Check again: a newer call can queue up while this one awaited stop().
        if (isStale?.()) {
            return;
        }

        await startConnection();

        // Check again: a newer call can also have started during the await above.
        if (isStale?.()) {
            return;
        }

        // Set before Leave/Join so a reconnect firing mid-switch rejoins the target chat, not the old one.
        joinedChatId = newChatId;
        if (oldChatId) {
            console.log('Leaving old chat:', oldChatId);
            await connection.invoke('LeaveChat', oldChatId).catch((err) => {
                console.error('Failed to leave chat:', oldChatId, err);
            });
        }

        if (newChatId) {
            console.log('Joining new chat:', newChatId);
            // Swallowed here, not rethrown: a failed join leaves the connection open but out of
            // the group, so hand off to the same retry rejoinChat uses after a dropped reconnect.
            await connection.invoke('JoinChat', newChatId).catch((err) => {
                console.error('Failed to join chat:', newChatId, err);
                rejoinChat(newChatId, 1);
            });
        }
    };

    const switchChat = (newChatId?: string, oldChatId?: string, isStale?: () => boolean) =>
        enqueue(() => runSwitchChat(newChatId, oldChatId, isStale));

    /**
     * Queued behind `switchChat` so an in-flight one can't complete after this and re-open the connection.
     * Clears `joinedChatId`, which `rejoinChat` checks before every retry, so a pending retry stops itself
     * instead of rejoining an abandoned chat.
     */
    const disconnect = () => {
        joinedChatId = undefined;

        return enqueue(async () => {
            if (connection.state !== 'Disconnected') {
                await stopConnection();
            }
        });
    };

    return { connection, switchChat, disconnect };
}
